/**
 * <door-log> — the doorperson's own instrument: how this entrance is doing,
 * and what its last scans actually did.
 *
 * IT REPLACES THE COUNTS PILL AND THE TWO SHEETS IT OPENED, and the reason
 * is structural rather than cosmetic. A `show()` overlay mounts into the
 * browser's top layer, above the verdict splash's `z-index: 40` in the
 * ordinary stacking context, so a sheet opened that way has to close itself
 * the moment a verdict resolves or the operator is scanning blind behind an
 * opaque panel — which makes the surface a doorperson needs during a dispute
 * the one surface guaranteed to vanish the instant the next guest presents a
 * ticket, exactly when a queue is forming behind the person arguing. This one
 * lives INSIDE the scan surface's own stacking context, so a verdict paints
 * over it and it never closes: the dispute gets resolved while the line keeps
 * moving. Nothing about this surface may go back through `show()`.
 *
 * A DISCLOSURE, not a sheet of our own. `<schmancy-details>` already owns
 * two rest positions and the grid-height animation between them, and the
 * scan screen's top-left door readout is already built from it — so the
 * console's own open/closed behaviour is the primitive's, not hand-rolled
 * motion over live video.
 *
 * THE RESTING STATE ALREADY ANSWERS THREE QUESTIONS. How many are in, how
 * many are still outside, and what the last scan did — the third being the
 * one the counts pill it replaces was silent on, and the only trace left
 * once the verdict splash has dismissed two seconds after the fact.
 *
 * PROPORTION, MASS AND A COLOUR RAIL CARRY WHAT SENTENCES WOULD. A ratio is
 * a length, so how much of the evening is in reads off a fill bar under the
 * count line rather than off two numbers a glance has to divide. The last
 * scan's outcome is a glyph in a tinted circle rather than a word beside a
 * dot, because a doorperson over a moving queue is reading a shape. Inside
 * the journal the same outcome is a rail down the row's leading edge — a
 * strip of colour an eye finds from the far end of the list.
 *
 * COLOUR MARKS ONLY WHAT WENT WRONG. On a real night better than nine in ten
 * rows are ordinary admissions; a green mark on each of them is the noise
 * that hides the one red row an operator is being asked about. So an
 * ordinary admission carries no colour at all — a `surface` circle in the
 * resting line, and no rail on its row — an already-in or an unrecorded scan
 * is `warning`, a refusal is `error`, and the journal's leading edge becomes
 * a column an eye finds a problem in without reading a word.
 *
 * A ROW IS AS TALL AS ITS DISPUTE. An admission is one line — the time and
 * whoever the ticket is for — because a row nobody will ever be asked about
 * has nothing to hold open. Everything else is the dispute row and keeps
 * three: the time, type and printed reference; the names; and the sentence
 * explaining what happened.
 *
 * NO FOOTER, and that is the structural change paying for itself. The two
 * overlays had to carry their own route to the manual-lookup screen because
 * they covered the scan surface whole, taking its "Can't scan? Get help"
 * affordance with it. A console that never occludes that corner needs no
 * second door to the same screen.
 *
 * WHO THE TICKET IS FOR, AND WHO PAID. The bare name is the live `Handover`
 * recipient — the friend the buyer tagged the ticket to — falling back to
 * the purchaser, because a ticket nobody handed on is for whoever bought it,
 * and a row carrying exactly one name needs no word telling the reader that
 * is whose ticket it is. `bought by:` renders only where the purchaser is
 * someone else, since one name printed twice is a line of noise on the row a
 * dispute is read off.
 * The two halves sit under two different grants (`tickets.checkin.scan` for
 * the handover, `tickets.reports.view` for the order), so a scan-only phone
 * renders the half it can read and no empty slot for the half it cannot:
 * absent, never disabled or redacted. `Ticket.ticketDisplayId` rides
 * beside them either way: it is the printed reference on the guest's own
 * ticket, wallet pass and confirmation mail, so it is the one handle this
 * door and that guest can match on when no name resolved at all.
 *
 * THE ANCESTOR PLACES IT. This component composes itself inside whatever box
 * it is given and carries no device prop: a door phone anchors it to the
 * bottom edge and leaves `open` to the operator's own thumb; the admin
 * console's check-in panel gives it a full-height column and pins it
 * `open locked`, which is `<schmancy-details>`'s own API rather than a
 * second layout.
 */
import type { SegmentedBarSegment } from "@mhmo91/schmancy/chart";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { computed } from "@mhmo91/schmancy/state";
import { virtualize } from "@mhmo91/schmancy/virtualizer";
import { translate } from "@momo/i18n";
import { Ticket } from "@momo/types";
import { css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin, uiLanguage } from "../../i18n/localize.ts";
import { ticketTypeName } from "../ticket-types/ticket-types.state.ts";
import { answeredTickets } from "../tickets/tickets.state.ts";
import { doorCounts, doorJournal, doorLanes } from "./door-log.state.ts";

const JUST_NOW_MS = 5 * 60_000;
const ONE_HOUR_MS = 60 * 60_000;
/** Below this, a second scan of a ticket this door already admitted is the
 *  same thumb firing twice rather than a ticket coming back round. */
const DOUBLE_SCAN_MS = 90_000;

/** The row heights `virtualize()`'s offset arithmetic is built on. Measured
 *  off the rendered rows rather than estimated: the windowed slice lands at
 *  an offset computed from these, so a guess drifts further the deeper the
 *  journal is scrolled, and a door's journal is scrolled deep exactly when
 *  it matters.
 *
 *  A scan row is not a fixed set of shapes but one per-line sum: `py-2`
 *  (16px) + one `body/lg` headline (24px) = 40px, plus 16px for each
 *  `body/sm` line the row actually carries — its name line, its detail line,
 *  or both. So a bare admission is 40px, an admission carrying the rest of
 *  its party 56px, a dispute row carrying name and explanation 72px, and a
 *  row that grows a line later grows by exactly one 16px step.
 *
 *  Each of those lines is `truncate`d, which is what keeps 16px a constant:
 *  an untruncated German type name wraps at phone width and every offset
 *  after it is computed from a height that row no longer has. A row's line
 *  count can only ever grow: a name read that settles after its scan adds
 *  the name line once and never removes it, so a row the operator is already
 *  looking at never shrinks under them. */
const BAND_PX = 30;
const SCAN_PX = 40;
const EXTRA_LINE_PX = 16;

type Scope = "all" | "refused";

type JournalRow =
	| { readonly kind: "band"; readonly key: string; readonly label: string }
	| {
			readonly kind: "scan";
			readonly key: string;
			readonly time: string;
			/** What the row's leading rail carries. `neutral` is the ordinary
			 *  admission, which carries no rail at all. */
			readonly tone: "neutral" | "warning" | "error";
			readonly toneLabel: string;
			/** The glyph and the tinted circle the resting line wears for this
			 *  outcome — mass where the journal's own rows use a rail. */
			readonly icon: string;
			readonly iconTone: "surface" | "warning" | "error";
			/** The row's own headline. On an admission that is whoever the ticket
			 *  is for — that half alone, never the buyer half — falling back to
			 *  the type name, and it is the admission's whole row. On everything
			 *  else it is the type name, over the two lines below. */
			readonly label: string;
			/** `<name> · bought by: …`, already composed and already narrowed to
			 *  the halves this door may read. Carried only by a row a dispute is
			 *  read off, where who paid is part of the answer; empty when neither
			 *  half resolved, and empty on an admission, whose headline carries
			 *  the holder half and deliberately drops the buyer half. */
			readonly name: string;
			readonly detail: string;
			readonly reference: string;
	  };

/** Every scan this device resolved, newest first, each already carrying the
 *  exact words its row renders — the view reads one projection and derives
 *  nothing of its own.
 *
 *  Lives in the view's own module scope rather than `door-log.state.ts`
 *  because every string below is a function of the reader's language, and a
 *  `.state.ts` file's import allow-list carries no localisation. */
const journalRows = computed<readonly JournalRow[]>(() => {
	const locale = uiLanguage();
	// h23 regardless of the locale's own preference: a door log is a
	// 24-hour context, and an AM/PM time overflows the fixed leading column
	// onto a second line.
	const clock = new Intl.DateTimeFormat(locale, {
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
	});
	const now = Date.now();
	const rows: JournalRow[] = [];
	let openBand = "";
	for (const scan of doorJournal.value) {
		const age = now - scan.atMillis;
		const hourStart = new Date(scan.atMillis).setMinutes(0, 0, 0);
		const band =
			age < JUST_NOW_MS
				? "just-now"
				: age < ONE_HOUR_MS
					? "this-hour"
					: String(hourStart);
		if (band !== openBand) {
			openBand = band;
			rows.push({
				kind: "band",
				key: `band:${band}`,
				label:
					band === "just-now"
						? translate(locale, "scanner.doorLog.band.justNow")
						: band === "this-hour"
							? translate(locale, "scanner.doorLog.band.thisHour")
							: `${clock.format(hourStart)} – ${clock.format(hourStart + ONE_HOUR_MS)}`,
			});
		}
		const typeName =
			scan.ticketId === null
				? null
				: (ticketTypeName(
						answeredTickets.get()?.byTicketId.get(scan.ticketId)?.typeId ?? "",
						locale,
					) ?? null);
		const reference =
			scan.ticketId === null ? "" : Ticket.ticketDisplayId(scan.ticketId);
		// A ticket nobody handed on is for the person who bought it, so the
		// purchaser fills the `For:` slot rather than opening a second one —
		// and then never repeats itself in `bought by:`.
		const forName = scan.holderName ?? scan.buyerName ?? "";
		const name =
			forName === "" || scan.buyerName === null || scan.buyerName === forName
				? forName
				: `${forName} · ${translate(locale, "scanner.doorLog.row.boughtBy", { name: scan.buyerName })}`;
		const verdict = scan.verdict;
		if (verdict.kind === "admit" || verdict.kind === "pending-admit") {
			const party = verdict.party;
			rows.push({
				kind: "scan",
				key: scan.key,
				time: clock.format(scan.atMillis),
				tone: "neutral",
				toneLabel: translate(locale, "scanner.doorLog.outcome.admitted"),
				icon: "check",
				iconTone: "surface",
				// An admission is one line, and the one thing worth reading on it
				// is who came in — so it carries `forName` alone, never the
				// composed `<name> · bought by: …`: at phone width that pair
				// truncates mid-string and delivers neither name, and who paid is
				// not what an admission is read for. The type name stands in only
				// where no name resolved at all, and the printed reference waits
				// for a row a dispute is actually being read off.
				label:
					forName === ""
						? (typeName ?? translate(locale, "scanner.doorLog.row.ticket"))
						: forName,
				name: "",
				detail:
					party !== null && party.othersRemaining > 0
						? translate(locale, "scanner.doorLog.detail.partyRemaining", {
								count: party.othersRemaining,
							})
						: "",
				reference: "",
			});
			continue;
		}
		if (verdict.kind === "already-in") {
			rows.push({
				kind: "scan",
				key: scan.key,
				time: clock.format(scan.atMillis),
				tone: "warning",
				toneLabel: translate(locale, "scanner.doorLog.outcome.alreadyIn"),
				icon: "warning",
				iconTone: "warning",
				label: typeName ?? translate(locale, "scanner.doorLog.row.ticket"),
				name,
				detail:
					verdict.redeemedAtMillis === null
						? translate(locale, "scanner.verdict.alreadyIn.stranger")
						: verdict.doorLabel !== null
							? translate(
									locale,
									"scanner.doorLog.detail.admittedAtOtherDoor",
									{
										at: clock.format(verdict.redeemedAtMillis),
										door: verdict.doorLabel,
									},
								)
							: scan.atMillis - verdict.redeemedAtMillis < DOUBLE_SCAN_MS
								? translate(locale, "scanner.doorLog.detail.justScanned")
								: translate(locale, "scanner.doorLog.detail.youAdmittedAt", {
										at: clock.format(verdict.redeemedAtMillis),
									}),
				reference,
			});
			continue;
		}
		if (verdict.kind === "held") {
			rows.push({
				kind: "scan",
				key: scan.key,
				time: clock.format(scan.atMillis),
				tone: "warning",
				toneLabel: translate(locale, "scanner.doorLog.outcome.notRecorded"),
				icon: "warning",
				iconTone: "warning",
				label: translate(locale, "scanner.doorLog.row.noAnswer"),
				name,
				detail: translate(
					locale,
					verdict.cause === "offline"
						? "scanner.doorLog.detail.heldOffline"
						: "scanner.doorLog.detail.heldTimeout",
				),
				reference,
			});
			continue;
		}
		if (verdict.kind === "stop") {
			// A code no ticket carries resolves no type, so the label falls back
			// to the very sentence the refusal itself carries — printing it twice
			// and paying a second line for the repeat.
			const stopLabel =
				typeName ?? translate(locale, "scanner.verdict.stop.unknown");
			rows.push({
				kind: "scan",
				key: scan.key,
				time: clock.format(scan.atMillis),
				tone: "error",
				toneLabel: translate(locale, "scanner.doorLog.outcome.refused"),
				icon: "error",
				iconTone: "error",
				label: stopLabel,
				name,
				// The sentence the splash already showed, verbatim — the resolving
				// sync translated it once and the journal repeats it rather than
				// paraphrasing the same refusal a second way.
				detail: verdict.detail === stopLabel ? "" : verdict.detail,
				reference,
			});
		}
	}
	return rows;
});

/** Everything that went other than cleanly, and nothing else — the one view a
 *  doorperson under pressure actually wants. The hour bands drop out with the
 *  admissions: six rows spanning twenty minutes is a list, and a band header
 *  over each of them would be chrome naming what the times already say. */
const refusedRows = computed<readonly JournalRow[]>(() =>
	journalRows
		.get()
		.filter((row) => row.kind === "scan" && row.tone !== "neutral"),
);

/** Which lanes are still moving, in the reader's own language — as one
 *  part-to-whole track rather than a row of name/number pairs, because how
 *  the queue outside divides across the lanes is a set of widths and only
 *  incidentally a set of figures. */
const laneSegments = computed<readonly SegmentedBarSegment[]>(() => {
	const locale = uiLanguage();
	return doorLanes.get().map((lane) => ({
		id: lane.typeId,
		label:
			ticketTypeName(lane.typeId, locale) ??
			translate(locale, "scanner.doorLog.otherLane"),
		value: lane.toCome,
	}));
});

@schmancyElement("door-log")
export default class DoorLog extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: grid;
				grid-template-rows: minmax(0, 1fr);
				min-height: 0;
				/* How tall the journal may grow before it starts scrolling. This is
				 * the ancestor's fact, not the component's — the box a door phone has
				 * under its camera and the box an admin panel has beside it are
				 * different numbers — so each mount overrides it and the value here is
				 * only a floor.
				 *
				 * KNOWN COMPROMISE. It exists at all because schmancy-details does not
				 * pass a height bound into its content: a bounded host, h-full on the
				 * details, and minmax(0, 1fr) rows on both sides still leave the
				 * slotted content sized to itself, so the journal grows past the sheet
				 * instead of scrolling inside it. The real fix belongs in the
				 * primitive, at packages/schmancy/src/details — whose source is not
				 * checked out in this tree, so it cannot be made here. This variable
				 * comes out the moment the primitive carries the bound. */
				--door-log-journal: 320px;
			}
		`,
	];

	/** The ancestor's placement decision, not a device class: a door phone
	 *  leaves both false and the operator's thumb drives them; a bounded
	 *  console panel with permanent room sets both. */
	@property({ type: Boolean }) open = false;
	@property({ type: Boolean }) locked = false;

	@state() private scope: Scope = "all";

	override render() {
		const counts = doorCounts.get();
		const numbers = new Intl.NumberFormat(this.uiLanguage);
		const rows = journalRows.get();
		const refused = refusedRows.get();
		const newest = rows.find((row) => row.kind === "scan");
		const live = answeredTickets.get()?.live ?? false;
		const shown = this.scope === "refused" ? refused : rows;

		return html`
			<schmancy-details
				type="containerLowest"
				class="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] rounded-none"
				?open=${this.open}
				?locked=${this.locked}
				?hide-indicator=${this.locked}
				summary-padding="px-4 pb-3 pt-3"
				content-padding="p-0"
				indicator-rotate="180"
				@toggle=${(e: CustomEvent<{ open: boolean }>) => {
					this.open = e.detail.open;
				}}
			>
				<!-- A chevron on a pinned console promises a collapse that cannot
				     happen; where the ancestor locked it open there is no second
				     rest position for it to point at. -->
				${when(
					!this.locked,
					() =>
						html`<schmancy-icon slot="indicator">keyboard_arrow_up</schmancy-icon>`,
				)}
				<div slot="summary" class="grid min-w-0 gap-2">
					<div class="grid min-w-0 gap-1.5">
						<div class="flex min-w-0 items-baseline gap-6">
							<div class="flex items-baseline gap-2">
								<schmancy-typography type="headline" token="md" class="tabular-nums">
									${
										counts === null
											? html`<schmancy-spinner size="sm"></schmancy-spinner>`
											: numbers.format(counts.admitted)
									}
								</schmancy-typography>
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
									${translate(this.uiLanguage, "scanner.doorLog.in")}
								</schmancy-typography>
							</div>
							<div class="flex items-baseline gap-2">
								<schmancy-typography type="title" token="md" class="tabular-nums text-surface-onVariant">
									${
										counts === null
											? html`<schmancy-spinner size="sm"></schmancy-spinner>`
											: numbers.format(counts.toCome)
									}
								</schmancy-typography>
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
									${translate(this.uiLanguage, "scanner.doorLog.stillOutside")}
								</schmancy-typography>
							</div>
						</div>
						<!-- How far through the evening this door is, as a length: the
						     two figures above are the same fact, and a length is the
						     one form of it a glance over a queue reads without doing
						     arithmetic. -->
						<schmancy-progress
							class="w-full"
							size="lg"
							color="primary"
							.value=${counts?.admitted ?? 0}
							.max=${counts?.expected ?? 0}
						></schmancy-progress>
					</div>

					<!-- One slot, three tenses — a stale readout, the last scan, or
					     the fact that nothing has been scanned yet. The numbers
					     above are quoted to supervisors, so a door reading a
					     cache-only index has to say so where the numbers are, not
					     in a corner of a different pill. -->
					${when(
						!live,
						() => html`
							<div class="flex min-w-0 items-center gap-3">
								<schmancy-tone-dot
									class="h-9 w-9"
									variant="soft"
									color="warning"
									icon="cloud_off"
									label=${translate(this.uiLanguage, "scanner.doorLog.notConnected")}
								></schmancy-tone-dot>
								<schmancy-typography type="body" token="md" class="min-w-0 truncate">
									${translate(this.uiLanguage, "scanner.doorLog.stale")}
								</schmancy-typography>
							</div>
						`,
						() =>
							newest !== undefined && newest.kind === "scan"
								? html`
										<div class="flex min-w-0 items-center gap-3">
											<!-- The outcome as mass rather than as a word: an icon in
											     a tinted circle, and on an ordinary admission an
											     untoned surface circle, so the shape carries the
											     reading and colour is still spent only on what went
											     wrong. -->
											<schmancy-tone-dot
												class="h-9 w-9"
												variant="soft"
												color=${newest.iconTone}
												icon=${newest.icon}
												label=${newest.toneLabel}
											></schmancy-tone-dot>
											<schmancy-typography type="label" token="md" class="shrink-0 tabular-nums text-surface-onVariant">
												${newest.time}
											</schmancy-typography>
											<!-- The sentence, and nothing beside it: on a row that went
											     wrong "Admitted 10:02 · Tür 2 — Seiteneingang" is what
											     the operator says out loud, and the printed reference is
											     one tap below in the journal, on a row wide enough to
											     carry both. -->
											<schmancy-typography type="body" token="md" class="min-w-0 truncate">
												${
													newest.tone === "neutral" || newest.detail === ""
														? newest.label
														: newest.detail
												}
											</schmancy-typography>
										</div>
									`
								: html`
										<schmancy-typography type="body" token="md" class="text-surface-onVariant">
											${translate(this.uiLanguage, "scanner.doorLog.nothingScanned")}
										</schmancy-typography>
									`,
					)}
				</div>

				<div class="grid min-h-0 w-full min-w-0 grid-rows-[auto_auto_minmax(0,1fr)] gap-3 pb-1">
					<!-- Which lanes are still moving, as one track: the shape of the
					     queue outside is what an operator glances for, and five rows
					     of figures would take the space the journal is open for. The
					     sum sits above already, so the bar carries no total of its
					     own.

					     The bar's OWN legend is off and the pair row below carries the
					     naming instead: at 393px five lanes wrap that legend onto
					     three lines — a swatch, a name, a count and a percentage each
					     — which is more of the sheet than the journal itself gets. The
					     pair row fits the same five lanes onto one line and a bit. -->
					<div class="grid gap-1.5 px-4">
						<schmancy-segmented-bar
							thickness="12"
							.total=${false}
							.legend=${false}
							.segments=${laneSegments.get()}
						></schmancy-segmented-bar>
						<!-- Visual restatement only. With the legend off the bar still
						     names its own segments to assistive tech, so leaving this row
						     readable would read the same five lanes and the same five
						     figures out a second time. The bar keeps the naming because it
						     is the element carrying the proportions; this row is that one
						     reading rendered as text for the eye. -->
						<div aria-hidden="true" class="flex flex-wrap items-baseline gap-x-4 gap-y-1">
							${repeat(
								laneSegments.get(),
								(lane) => lane.id,
								(lane) => html`
									<div class="flex items-baseline gap-1.5">
										<schmancy-typography type="label" token="md" class="text-surface-onVariant">
											${lane.label}
										</schmancy-typography>
										<schmancy-typography type="label" token="md" class="tabular-nums">
											${numbers.format(lane.value)}
										</schmancy-typography>
									</div>
								`,
							)}
						</div>
					</div>

					<!-- Renders only once there is something to separate: on a clean
					     shift every row is an admission and a scope switch offers no
					     choice at all. -->
					${when(
						refused.length > 0,
						() => html`
							<schmancy-chips
								class="w-full"
								inline-padding="px-4"
								required
								.value=${this.scope}
								@change=${(e: CustomEvent<{ value: string }>) => {
									if (e.detail.value === this.scope) return;
									this.scope = e.detail.value === "refused" ? "refused" : "all";
								}}
							>
								<schmancy-filter-chip value="all">
									${translate(this.uiLanguage, "scanner.doorLog.scope.all")}
								</schmancy-filter-chip>
								<schmancy-filter-chip value="refused">
									${translate(
										this.uiLanguage,
										"scanner.doorLog.scope.refused",
										{
											count: refused.length,
										},
									)}
								</schmancy-filter-chip>
							</schmancy-chips>
						`,
					)}

					${when(
						shown.length === 0,
						// Never a bare "nothing here": a door reads one of two very
						// different situations off an empty journal — a phone that has not
						// scanned anything yet, and a shift where nothing has gone wrong —
						// and only the second is worth being pleased about.
						() => html`
							<schmancy-typography type="body" token="md" class="px-4 pb-2 text-surface-onVariant">
								${translate(
									this.uiLanguage,
									this.scope === "refused"
										? "scanner.doorLog.empty.refused"
										: "scanner.doorLog.empty.all",
								)}
							</schmancy-typography>
						`,
						() => html`
							<div ${overflowWithin()} role="list" class="max-h-[var(--door-log-journal)] min-h-0">
								${virtualize(
									shown,
									(row) => row.key,
									(row) =>
										row.kind === "band"
											? html`
													<div role="listitem" class="flex h-[30px] items-center px-4">
														<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
															${row.label}
														</schmancy-typography>
													</div>
												`
											: html`
													<div
														role="listitem"
														class="grid grid-cols-[2.75rem_0.25rem_1fr_auto] grid-rows-[auto_auto_auto] items-stretch gap-x-3 px-4 py-2"
													>
														<schmancy-typography
															type="label"
															token="lg"
															class="col-start-1 self-center tabular-nums text-surface-onVariant"
														>
															${row.time}
														</schmancy-typography>
														<!-- The rail, and only where there is something to
														     find: an ordinary admission leaves the column
														     empty, so what colour there is down the journal's
														     leading edge is exactly what went wrong. -->
														${when(
															row.tone !== "neutral",
															() => html`
																<schmancy-tone-spine
																	class="col-start-2 row-span-full w-1"
																	variant="solid"
																	color=${row.tone}
																></schmancy-tone-spine>
															`,
														)}
														<schmancy-typography
															type="body"
															token="lg"
															class="col-start-3 min-w-0 self-center truncate"
														>
															${row.label}
														</schmancy-typography>
														${when(
															row.reference !== "",
															() => html`
																<schmancy-typography
																	type="label"
																	token="sm"
																	class="col-start-4 self-center tabular-nums text-surface-onVariant"
																>
																	${row.reference}
																</schmancy-typography>
															`,
														)}
														<!-- Who the ticket is for, and who paid — under the
														     type name rather than beside it, because two
														     personal names plus a type name plus a reference
														     on one line is three of them ellipsed. Spans the
														     label AND reference columns for the same reason
														     the explanation below it does. -->
														${when(
															row.name !== "",
															() => html`
																<schmancy-typography
																	type="body"
																	token="sm"
																	class="col-start-3 col-end-5 row-start-2 truncate"
																>
																	${row.name}
																</schmancy-typography>
															`,
														)}
														<!-- The explanation spans the label AND reference columns rather
														     than sitting inside the label's own: "Already in 10:43 · Tür 2
														     — Seiteneingang" is the whole answer to the dispute, and the
														     door's name is the half that gets cut when it has to share a
														     line's width with a reference sitting beside it. -->
														${when(
															row.detail !== "",
															() => html`
																<schmancy-typography
																	type="body"
																	token="sm"
																	class="col-start-3 col-end-5 row-start-3 truncate text-surface-onVariant"
																>
																	${row.detail}
																</schmancy-typography>
															`,
														)}
													</div>
												`,
									{
										estimateSize: (index) => {
											const row = shown[index];
											if (row === undefined || row.kind === "band")
												return BAND_PX;
											return (
												SCAN_PX +
												(row.name === "" ? 0 : EXTRA_LINE_PX) +
												(row.detail === "" ? 0 : EXTRA_LINE_PX)
											);
										},
									},
								)}
							</div>
						`,
					)}

				</div>
			</schmancy-details>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"door-log": DoorLog;
	}
}
